import {
  Grid,
  Text,
  Flex,
  Button,
  Box,
  useTheme,
  Tooltip,
} from '@chakra-ui/react';
import { ReactComponent as DoctorMaleSVG } from '../../../logos/doctor_male.svg';
import { ReactComponent as DoctorFemaleSVG } from '../../../logos/doctor_female.svg';
import { ReactComponent as ClinicSVG } from '../../../logos/clinic.svg';
import { ReactComponent as LocationSVG } from '../../../logos/location.svg';
import { ReactComponent as StarFilledSVG } from '../../../logos/starFilled.svg';
import { ReactComponent as PhoneSVG } from '../../../logos/phone.svg';
import SeeWhyModal from './SeeWhy';
import { useState } from 'react';
import { toTitleCase, formatPhoneNumber } from '../../../utils';
import { SIMULATED_DATA_TEXT } from '../../../config';

const ProviderCard = ({ provider, searchParameters }) => {
  const providerFacilities = provider.FACILITY_AFFILIATION_LIST?.split('|');
  const theme = useTheme();
  const [isSeeWhyModalOpen, setSeeWhyModalOpen] = useState(false);
  const openSeeWhyModal = () => setSeeWhyModalOpen(true);
  const closeSeeWhyModal = () => setSeeWhyModalOpen(false);

  const InfoRow = ({ icon: Icon, text }) => (
    <Flex
      justifyContent='space-between'
      alignItems='center'
      borderBottom='1px solid #DDE8EE'
      marginTop='12px'
    >
      <Icon width='14px' height='14px' />
      <Text>{text}</Text>
    </Flex>
  );

  return (
    <Grid
      width='98%'
      marginTop='10px'
      bgColor={theme.colors?.bgGrey2}
      templateColumns='75% 25%'
      gap='4px'
      color={theme.colors?.smoky}
      padding='7px 15px'
      borderRadius='10px'
    >
      <Flex flexDirection='column' justifyContent='space-between'>
        <Flex>
          {provider.GENDER_CD === 'M' ? (
            <DoctorMaleSVG width='40px' height='40px' />
          ) : (
            <DoctorFemaleSVG width='40px' />
          )}
          <Flex flexDirection='column' marginLeft='8px'>
            <Text color={theme.colors?.darkBlue} fontWeight='600'>
              {toTitleCase(provider.FIRST_NM)} {toTitleCase(provider.LAST_NM)}
            </Text>
            <Flex justifyContent='space-between' fontSize='1rem'>
              <Text>
                <em>Specialty:</em>{' '}
                <strong style={{ marginLeft: '3px' }}>
                  {provider.PROVIDER_SPECIALTY_NM}
                </strong>
              </Text>
              <Text marginLeft='5px'>
                <em>Subspecialty:</em>
                <Text as='span' display='inline' marginLeft='3px'>
                  {provider.PROVIDER_TAXONOMY_CLASSIFICATION_NM}
                </Text>
              </Text>
            </Flex>
            <Box marginTop='5px' fontSize='1rem'>
              {providerFacilities && (
                <Flex alignItems='center'>
                  <Box marginRight='5px'>
                    <ClinicSVG />
                  </Box>
                  <Text>{toTitleCase(providerFacilities[0])}</Text>
                  {providerFacilities.length > 1 && <Text>+1</Text>}
                </Flex>
              )}
              <Flex alignItems='center'>
                <Box marginRight='5px'>
                  <LocationSVG />
                </Box>
                <Text>
                  {toTitleCase(
                    `${provider.STREET_ADDRESS}, ${provider.CITY_NM}, ${provider.STATE_CD}`,
                  )}
                </Text>
              </Flex>
            </Box>
          </Flex>
        </Flex>
        <Flex width='100%' fontSize='0.7rem' marginLeft='5px' marginTop='5px'>
          {provider.YEARSOFEXPERIENCE && (
            <Flex
              bgColor='white'
              padding='2px 4px'
              borderRadius='4px'
              height='2rem'
              alignItems='center'
            >
              <Text>Years of Experience </Text>
              <Text
                color={theme.colors?.blueOne}
                fontWeight='600'
                marginLeft='4px'
              >
                {provider.YEARSOFEXPERIENCE > 21
                  ? '21+'
                  : provider.YEARSOFEXPERIENCE}
              </Text>
            </Flex>
          )}
          <Flex
            bgColor='white'
            borderRadius='4px'
            justifyContent='space-evenly'
            marginLeft='5px'
            height='2rem'
            sx={{
              '& > *': {
                marginLeft: '5px',
              },
            }}
            alignItems='center'
          >
            <StarFilledSVG />
            <Text>We recommend this doctor for your consult.</Text>
            <Text
              as='b'
              cursor='pointer'
              marginRight='5px'
              color={theme.colors?.gold}
              fontWeight='600'
              onClick={openSeeWhyModal}
            >
              See Why
            </Text>
          </Flex>
        </Flex>
      </Flex>
      <Flex
        flexDirection='column'
        justifyContent='space-between'
        fontSize='1rem'
      >
        <Flex flexDirection='column'>
          {provider.DISTANCE_FROM && (
            <InfoRow
              icon={LocationSVG}
              text={`${provider.DISTANCE_FROM.toFixed(2)} miles away`}
            />
          )}
          {provider.PHN_NUMBER && (
            <InfoRow
              icon={PhoneSVG}
              text={formatPhoneNumber(provider.PHN_NUMBER)}
            />
          )}
        </Flex>
        <Flex
          bgColor={theme.colors?.explorerTableHeaderColor}
          alignItems='center'
          justifyContent='space-around'
          marginTop='12px'
        >
          <Box
            width='10px'
            height='10px'
            borderRadius='50%'
            backgroundColor={theme.colors?.greenExplorer}
          />
          <Flex alignItems='center' cursor='help' fontSize='0.7rem'>
            <Text>Accepting new patients</Text>
            <Tooltip
              label={SIMULATED_DATA_TEXT}
              fontSize='0.9rem'
              placement='top'
              hasArrow
            >
              <Box
                ml='2'
                bgColor={theme.colors?.blueTwo}
                borderRadius='50%'
                width='15px'
                height='15px'
                display='flex'
                alignItems='center'
                justifyContent='center'
                cursor='help'
              >
                <Text color='white'>?</Text>
              </Box>
            </Tooltip>
          </Flex>
        </Flex>
        <Button
          as='a'
          href={provider.CJ_HOME_LINK}
          target='_blank'
          bgColor={theme.colors?.blueTwo}
          color='white'
          fontSize='0.9rem'
          marginTop='10px'
          height='2rem'
          width='100%'
        >
          View Profile
        </Button>
      </Flex>
      <SeeWhyModal
        isOpen={isSeeWhyModalOpen}
        onClose={closeSeeWhyModal}
        searchParameters={searchParameters}
        provider={provider}
      />
    </Grid>
  );
};

export default ProviderCard;
